"use client";

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { BackendLabel } from "./backend-label";
import { backendTextareaClass } from "./backend-ui";
import ValidationPopover, {
  type ValidationErrorT,
  type ValidationPopoverBgT,
} from "../ui/validation-popover";

export interface BackendTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?:
    | boolean
    | string
    | string[]
    | ValidationErrorT[]
    | (string | ValidationErrorT)[];
  containerClassName?: string;
  bgVariant?: ValidationPopoverBgT;
  triggerOnHover?: boolean;
  autoResize?: boolean;
  maxAutoResizeHeight?: number;
}

export interface BackendTextareaWithLabelProps extends BackendTextareaProps {
  label?: string;
  labelClassName?: string;
}

const BackendTextarea = forwardRef<HTMLTextAreaElement, BackendTextareaProps>(
  (
    {
      className,
      error = false,
      containerClassName,
      bgVariant = "accent",
      triggerOnHover = false,
      autoResize = false,
      maxAutoResizeHeight,
      ...props
    },
    ref
  ) => {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const setMergedRef = useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    const textareaStyles = cn(
      backendTextareaClass,
      error && "border-destructive focus:border-destructive",
      className
    );

    const hasError = Boolean(error);

    let errorMessages: (string | ValidationErrorT)[] = [];
    if (typeof error === "string" && error) {
      errorMessages = [error];
    } else if (Array.isArray(error)) {
      errorMessages = error.filter((item) => {
        if (typeof item === "string") return item;
        if (typeof item === "object" && "message" in item) {
          return (item as ValidationErrorT).message;
        }
        return false;
      });
    }

    const shouldShowValidation = hasError && errorMessages.length > 0;

    useEffect(() => {
      if (!shouldShowValidation && popoverOpen) {
        setPopoverOpen(false);
      }
    }, [shouldShowValidation, popoverOpen]);

    const resizeTextarea = useCallback(() => {
      if (!autoResize || !textareaRef.current) return;

      const textarea = textareaRef.current;
      textarea.style.height = "auto";

      const computedStyles = window.getComputedStyle(textarea);
      const minHeight = Number.parseFloat(computedStyles.minHeight) || 100;
      const maxHeight = maxAutoResizeHeight ?? minHeight * 2;
      const nextHeight = Math.min(
        Math.max(textarea.scrollHeight, minHeight),
        maxHeight
      );

      textarea.style.height = `${nextHeight}px`;
      textarea.style.overflowY =
        textarea.scrollHeight > maxHeight ? "auto" : "hidden";
    }, [autoResize, maxAutoResizeHeight]);

    useEffect(() => {
      resizeTextarea();
    }, [resizeTextarea, props.value]);

    const handleClick = (event: React.MouseEvent<HTMLTextAreaElement>) => {
      if (shouldShowValidation && !popoverOpen) {
        setPopoverOpen(true);
      }
      props.onClick?.(event);
    };

    const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      props.onFocus?.(event);
    };

    const handleTouchStart = (event: React.TouchEvent<HTMLTextAreaElement>) => {
      if (shouldShowValidation && !popoverOpen) {
        setPopoverOpen(true);
      }
      props.onTouchStart?.(event);
    };

    const handleInput = (event: React.InputEvent<HTMLTextAreaElement>) => {
      if (autoResize) {
        resizeTextarea();
      }
      props.onInput?.(event);
    };

    return (
      <div className="flex w-full flex-col">
        <div className={cn("relative text-sm", containerClassName)}>
          <ValidationPopover
            error={errorMessages}
            bgVariant={bgVariant}
            triggerOnHover={triggerOnHover}
            triggerRef={ref}
            isOpen={shouldShowValidation ? popoverOpen : false}
            onOpenChange={shouldShowValidation ? setPopoverOpen : undefined}
          >
            <textarea
              data-slot="backend-textarea"
              className={textareaStyles}
              ref={setMergedRef}
              aria-invalid={hasError || undefined}
              {...props}
              onClick={handleClick}
              onFocus={handleFocus}
              onTouchStart={handleTouchStart}
              onInput={handleInput}
            />
          </ValidationPopover>
        </div>
      </div>
    );
  }
);

const BackendTextareaWithLabel = forwardRef<
  HTMLTextAreaElement,
  BackendTextareaWithLabelProps
>(({ className, label, labelClassName, id, required, ...props }, ref) => {
  return (
    <div className="flex flex-col gap-1 sm:gap-1.5">
      {label && (
        <BackendLabel
          htmlFor={id}
          required={required}
          className={labelClassName}
        >
          {label}
        </BackendLabel>
      )}
      <BackendTextarea id={id} className={className} ref={ref} {...props} />
    </div>
  );
});

BackendTextarea.displayName = "BackendTextarea";
BackendTextareaWithLabel.displayName = "BackendTextareaWithLabel";

export { BackendTextarea, BackendTextareaWithLabel };
